/**
 * The side panel's first section: the active tool's options (every tool has one), brought into view and lit
 * briefly when the tool changes; and the roof check ("Close gaps": what is open, fix one or all). Presentational;
 * SketchEditor passes state and callbacks. Styling: sketch.css (`.sk-toolsec…`, `.sk-gaps…`).
 */
import { Check, CircleAlert, Crosshair, ShieldCheck, Wand2, X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import type { DormerKind } from "./dormers.ts";
import { gapSummary, gapText, type Gap } from "./gaps.ts";
import { DormerGlyph, TOOL_KEYS, TOOL_LABELS, ToolIcon, VariantGlyph } from "./SketchToolbar";
import type { MessageKey } from "../i18n/keys";
import { structureLabel } from "./structures.ts";
import type { SketchTool } from "./types.ts";
import { useSketchText } from "./useText.ts";
import { nextObstacle, nextTree, obstacleDims, variantsOf, type ShadeOptions, type ToolState } from "./variants.ts";

/** Wheel gestures as the cheat sheet binds them (e.g. "Shift", "Alt", "Shift Alt"). */
export interface WheelKeys {
  size: string;
  kind: string;
  height: string;
}

function Keys({ combo }: { combo: string }) {
  return (
    <span className="sk-keys">
      {combo.split(" ").map((k) => <kbd key={k} className="sk-kbd">{k}</kbd>)}
    </span>
  );
}

/** The active tool's section. Its key is the tool, so it mounts again (and lights up) on every change. */
export function ToolSection(props: {
  tool: SketchTool;
  state: Omit<ToolState, "tool">;
  readOnly: boolean;
  wheel: WheelKeys;
  structureName: string;
  notes: number;
  onVariant: (tool: SketchTool, id: string) => void;
  onShade: (o: Partial<ShadeOptions>) => void;
  /** Panels: the tool's options are the Panels section itself (passed in). */
  panels?: ReactNode;
}) {
  const tx = useSketchText();
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const side = ref.current?.closest(".sk-side");
    if (side && side.scrollTop > 0) side.scrollTo({ top: 0, behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }, []);
  const { tool, state } = props;
  const sh = state.shade;
  const variants = variantsOf(tool, state);
  const chips = (labelKey: MessageKey) => {
    const label = tx.t(labelKey);
    return (
      variants.length > 0 && (
      <div className="sk-field">
        <span className="sk-label">{label}</span>
        <div className="sk-chips" role="radiogroup" aria-label={label}>
          {variants.map((v) => (
            <button key={v.id} type="button" role="radio" aria-checked={v.active} className={`sk-chip sk-variant-chip${v.active ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => props.onVariant(tool, v.id)}>
              <VariantGlyph tool={tool} id={v.id} /> {tx.t(v.label)}
            </button>
          ))}
        </div>
      </div>
    ));
  };
  const plusWheel = (combo: string) => <span className="sk-wheel-combo"><Keys combo={combo} /> {tx.t("sketch.wheel.plus")}</span>;
  const wheelTips = (what: "tree" | "obstacle") => (
    <ul className="sk-wheel-tips" aria-label={tx.t("sketch.wheel.label")}>
      <li>{plusWheel(props.wheel.size)}<span>{tx.t(what === "tree" ? "sketch.wheel.crownSize" : "sketch.wheel.size")}</span></li>
      <li>{plusWheel(props.wheel.kind)}<span>{tx.t(what === "tree" ? "sketch.wheel.type" : "sketch.wheel.kind")}</span></li>
      <li>{plusWheel(props.wheel.height)}<span>{tx.t("sketch.wheel.height")}</span></li>
    </ul>
  );
  const number = (labelKey: MessageKey, value: number, unit: string, step: number, min: number, max: number, onChange: (v: number) => void) => (
    <span className="sk-number-wrap">
      <input className="sk-number" type="number" min={min} max={max} step={step} value={Math.round(value * 100) / 100} disabled={props.readOnly} aria-label={tx.t(labelKey)} onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || min)))} />
      <span className="sk-unit">{unit}</span>
    </span>
  );
  let body: ReactNode = null;
  let caption: ReactNode = null;
  if (tool === "panel" && props.panels) return <div className="sk-toolsec-wrap" key="panel">{props.panels}</div>;
  switch (tool) {
    case "select":
      caption = tx.t("sketch.toolsec.select.caption");
      body = (
        <ul className="sk-wheel-tips" aria-label={tx.t("sketch.wheel.label")}>
          <li>{plusWheel(props.wheel.size)}<span>{tx.t("sketch.wheel.sizeTurn")}</span></li>
          <li>{plusWheel(props.wheel.kind)}<span>{tx.t("sketch.wheel.kindOrientation")}</span></li>
          <li>{plusWheel(props.wheel.height)}<span>{tx.t("sketch.wheel.height")}</span></li>
        </ul>
      );
      break;
    case "pan":
      caption = tx.t("sketch.toolsec.pan.caption");
      break;
    case "footprint":
      caption = tx.t("sketch.toolsec.footprint.caption", { name: structureLabel(tx, props.structureName) });
      body = (
        <ul className="sk-wheel-tips">
          <li><span className="sk-wheel-combo"><Keys combo="Enter" /></span><span>{tx.t("sketch.keys.closeShape")}</span></li>
          <li><span className="sk-wheel-combo"><Keys combo="Alt" /></span><span>{tx.t("sketch.keys.anyAngleNoSnaps")}</span></li>
          <li><span className="sk-wheel-combo"><Keys combo="Esc" /></span><span>{tx.t("sketch.keys.startOver")}</span></li>
        </ul>
      );
      break;
    case "line":
      caption = tx.t("sketch.toolsec.line.caption");
      body = chips("sketch.toolsec.lineType");
      break;
    case "dormer":
      caption = tx.t("sketch.toolsec.dormer.caption");
      body = (
        <div className="sk-field">
          <span className="sk-label">{tx.t("sketch.toolsec.kind")}</span>
          <div className="sk-chips" role="radiogroup" aria-label={tx.t("sketch.toolsec.dormer.kinds")}>
            {(["gable", "hip", "shed"] as DormerKind[]).map((k) => (
              <button key={k} type="button" role="radio" aria-checked={state.dormer === k} className={`sk-chip sk-variant-chip${state.dormer === k ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => props.onVariant("dormer", k)}>
                <DormerGlyph kind={k} /> {tx.t(k === "gable" ? "sketch.dormer.gable" : k === "hip" ? "sketch.dormer.hip" : "sketch.dormer.shed")}
              </button>
            ))}
          </div>
        </div>
      );
      break;
    case "obstacle": {
      const next = nextObstacle(sh, [0, 0]);
      const [w] = obstacleDims(next);
      caption = tx.t("sketch.toolsec.obstacle.caption");
      body = (
        <>
          {chips("sketch.toolsec.kind")}
          <div className="sk-field">
            <span className="sk-label">{tx.t("sketch.toolsec.sizeHeight")} <span>{tx.t("sketch.toolsec.theNextOne")}</span></span>
            <div className="sk-inline">
              {number(sh.shape === "cylinder" ? "sketch.toolsec.diameterLabel" : "sketch.toolsec.sideLabel", w, tx.t(sh.shape === "cylinder" ? "sketch.toolsec.unitDiameter" : "sketch.toolsec.unitSide"), 0.05, 0.1, 6, (v) => props.onShade({ obstacleSize: v }))}
              {number("sketch.toolsec.heightAboveRoofLabel", next.height_m ?? 0, tx.t("sketch.toolsec.unitHigh"), 0.1, 0, 10, (v) => props.onShade({ obstacleHeight: v }))}
            </div>
            {(sh.obstacleSize != null || sh.obstacleHeight != null) && (
              <button type="button" className="sk-link" onClick={() => props.onShade({ obstacleSize: null, obstacleHeight: null })}>
                {tx.t("sketch.toolsec.usualSize", { kind: sh.obstacle })}
              </button>
            )}
          </div>
          {wheelTips("obstacle")}
        </>
      );
      break;
    }
    case "tree": {
      const next = nextTree(sh, [0, 0]);
      caption = tx.t("sketch.toolsec.tree.caption");
      body = (
        <>
          {chips("sketch.toolsec.type")}
          <div className="sk-field">
            <span className="sk-label">{tx.t("sketch.toolsec.crownHeight")} <span>{tx.t("sketch.toolsec.theNextOne")}</span></span>
            <div className="sk-inline">
              {number("sketch.toolsec.crownDiameterLabel", 2 * (next.radius ?? 3), tx.t("sketch.toolsec.unitWide"), 0.5, 0.4, 60, (v) => props.onShade({ treeRadius: v / 2 }))}
              {number("sketch.toolsec.treeHeightLabel", next.height, tx.t("sketch.toolsec.unitTall"), 0.5, 1, 120, (v) => props.onShade({ treeHeight: v }))}
            </div>
          </div>
          {wheelTips("tree")}
        </>
      );
      break;
    }
    case "neighbour":
      caption = tx.t(sh.neighbour === "building" ? "sketch.toolsec.neighbour.building" : "sketch.toolsec.neighbour.wall");
      body = (
        <>
          {chips("sketch.toolsec.draw")}
          <div className="sk-field">
            <span className="sk-label">{tx.t("sketch.toolsec.height")} <span>{tx.t("sketch.toolsec.theNextOne")}</span></span>
            <div className="sk-inline">{number("sketch.toolsec.heightLabel", sh.neighbourHeight ?? (sh.neighbour === "wall" ? 2 : 6), "m", 0.5, 0.5, 120, (v) => props.onShade({ neighbourHeight: v }))}</div>
          </div>
        </>
      );
      break;
    case "note":
      caption = tx.t("sketch.toolsec.note.caption");
      body = <span className="sk-eyebrow">{tx.t("sketch.toolsec.note.count", { count: props.notes })}</span>;
      break;
  }
  return (
    <section ref={ref} className="sk-side-section sk-toolsec" key={tool} aria-label={tx.t("sketch.toolsec.options", { tool: tx.t(TOOL_LABELS[tool]) })}>
      <div className="sk-side-head">
        <span className="sk-side-icon is-tool"><ToolIcon tool={tool} size={16} /></span>
        <h3>{tx.t(TOOL_LABELS[tool])}</h3>
        <span className="sk-eyebrow">{tx.t("sketch.toolsec.tool")} · <kbd className="sk-kbd">{TOOL_KEYS[tool]}</kbd></span>
      </div>
      {caption && <p className="sk-caption">{caption}</p>}
      {body}
    </section>
  );
}

/** The roof check: the gaps found (numbered as on the map), fix one, fix all, or close the check. */
export function GapsSection(props: { gaps: Gap[]; selected: string | null; readOnly: boolean; justClosed: number | null; onSelect: (id: string | null) => void; onFix: (g: Gap) => void; onFixAll: () => void; onClose: () => void }) {
  const tx = useSketchText();
  const ref = useRef<HTMLElement>(null);
  useEffect(() => ref.current?.scrollIntoView?.({ block: "nearest" }), []);
  const fixable = props.gaps.filter((g) => g.fixable).length;
  const closed = !props.gaps.length;
  return (
    <section ref={ref} className={`sk-side-section sk-gaps${closed ? " is-closed" : ""}`} aria-label={tx.t("sketch.gaps.label")}>
      <div className="sk-side-head">
        <span className={`sk-side-icon ${closed ? "is-ok" : "is-gap"}`}>{closed ? <ShieldCheck size={16} /> : <CircleAlert size={16} />}</span>
        <h3>{gapSummary(tx, props.gaps)}</h3>
        <button type="button" className="sk-icon-action" onClick={props.onClose} aria-label={tx.t("sketch.gaps.close")} title={tx.t("sketch.gaps.close")}><X size={15} /></button>
      </div>
      {closed ? (
        <p className="sk-caption">{props.justClosed ? tx.t("sketch.gaps.closedCount", { count: props.justClosed }) : tx.t("sketch.gaps.allClosed")}</p>
      ) : (
        <>
          <p className="sk-caption">{tx.t("sketch.gaps.intro")}</p>
          <ol className="sk-gap-list">
            {props.gaps.map((g, i) => {
              const t = gapText(tx, g);
              return (
                <li key={g.id} className={`sk-gap-row${props.selected === g.id ? " is-selected" : ""}`}>
                  <button type="button" className="sk-gap-pick" onClick={() => props.onSelect(props.selected === g.id ? null : g.id)} title={tx.t("sketch.gaps.locate")}>
                    <span className="sk-gap-n">{i + 1}</span>
                    <span className="sk-gap-text"><b>{t.title}</b><span>{t.detail}</span></span>
                    <Crosshair size={14} className="sk-gap-locate" aria-hidden />
                  </button>
                  <button type="button" className="sk-action sk-gap-fix" disabled={props.readOnly || !g.fixable} onClick={() => props.onFix(g)} title={tx.t(g.fixable ? "sketch.gaps.fixOne" : "sketch.gaps.needsLook")}>
                    {g.fixable ? <><Check size={14} /> {tx.t("sketch.gaps.fix")}</> : tx.t("sketch.gaps.check")}
                  </button>
                </li>
              );
            })}
          </ol>
          <div className="sk-actions">
            <button type="button" className="sk-action is-primary is-grow" disabled={props.readOnly || !fixable} onClick={props.onFixAll}>
              <Wand2 size={15} /> {fixable === props.gaps.length ? tx.t("sketch.gaps.closeAll", { count: fixable }) : tx.t("sketch.gaps.closeSome", { count: fixable, total: props.gaps.length })}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

